import { DigestCategoryCount } from "@/types/activity";
import { ConversationDetails, IssueCategory, ISSUE_CATEGORY_LABELS } from "@/types/conversation/conversation";
import { buildCategoryBreakdown } from "../utils/aggregates";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface InsightsCategoryBreakdownProps {
  conversations: ConversationDetails[];
  /** AI digest's classification stats — preferred when present, since it also covers conversations not yet analyzed for category. */
  digestBreakdown?: DigestCategoryCount[];
  /** True while the digest call is in flight and hasn't returned classification stats yet — shows a skeleton instead of the (possibly incomplete) client aggregate. */
  isLoading?: boolean;
}

const categoryLabel = (category: string) =>
  ISSUE_CATEGORY_LABELS[category as IssueCategory] ?? category;

export const InsightsCategoryBreakdown = ({
  conversations,
  digestBreakdown,
  isLoading,
}: InsightsCategoryBreakdownProps) => {
  const breakdown = digestBreakdown?.length
    ? digestBreakdown
    : buildCategoryBreakdown(conversations).map((b) => ({ category: b.category as string, count: b.count }));
  const maxCount = breakdown.reduce((m, b) => Math.max(m, b.count), 0);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden h-full">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
        <GenieIcon
          name="chat"
          size={24}
          color="#4E9CFF"
          secondaryColor="#FF4DB8"
          gradientEnabled
          label="Issue categories"
        />
        <div>
          <h3 className="text-base font-semibold text-gray-900">Issue categories</h3>
          <p className="text-xs text-gray-500">What kind of calls customers are making</p>
        </div>
      </div>

      <div className="p-5">
        {isLoading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="space-y-1.5">
                <div className="h-3.5 w-1/3 bg-gray-100 rounded animate-pulse" />
                <div className="h-1.5 w-full bg-gray-100 rounded-full animate-pulse" />
              </div>
            ))}
          </div>
        ) : breakdown.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">
              No categories extracted yet. Run analysis on a few conversations to see this breakdown.
            </p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {breakdown.map((b) => {
              const widthPct = Math.max(8, (b.count / maxCount) * 100);
              return (
                <div key={b.category}>
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {categoryLabel(b.category)}
                    </span>
                    <span className="text-xs text-gray-600 tabular-nums flex-shrink-0">
                      {b.count} call{b.count === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]"
                      style={{ width: `${widthPct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
